"use client";

import { useRouter, usePathname } from "next/navigation";
import { useLocale, useTranslations } from "next-intl";
import { locales, localeEndonyms } from "../../../i18n";
import * as React from "react";
import * as SelectPrimitive from "@radix-ui/react-select";
import { cn } from "@/lib/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { CircleFlag } from "react-circle-flags";

// Map locales to ISO country codes for react-circle-flags
const localeToCountryCode: Record<string, string> = {
  fr: "fr", // French (France)
  en: "gb", // English (Great Britain/UK)
  es: "es", // Spanish (Spain)
  de: "de", // German (Germany)
  it: "it", // Italian (Italy)
  pt: "pt", // Portuguese (Portugal)
  nl: "nl", // Dutch (Netherlands)
};

// Helper function to set a cookie
const setCookie = (name: string, value: string, days: number) => {
  const maxAge = days * 24 * 60 * 60;
  document.cookie = `${name}=${value};path=/;max-age=${maxAge};SameSite=Lax`;
};

// Custom SelectTrigger without the ChevronDown arrow
const CustomSelectTrigger = React.forwardRef<
  React.ElementRef<typeof SelectPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger>
>(({ className, children, ...props }, ref) => (
  <SelectPrimitive.Trigger
    ref={ref}
    className={cn(
      "flex h-9 items-center justify-center whitespace-nowrap rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm ring-offset-background data-[placeholder]:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring disabled:cursor-not-allowed disabled:opacity-50 [&>span]:line-clamp-1",
      className
    )}
    {...props}
  >
    {children}
  </SelectPrimitive.Trigger>
));
CustomSelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

export default function LanguageSwitcher() {
  const router = useRouter();
  const pathname = usePathname();
  const currentLocale = useLocale();
  const t = useTranslations("LanguageSwitcher");

  // Add this log to see what the client thinks the locale is on each render
  const handleLocaleChange = (newLocale: string) => {
    if (locales.includes(newLocale as any)) {
      let newPathname = pathname;
      if (pathname.startsWith(`/${currentLocale}`)) {
        newPathname = pathname.replace(`/${currentLocale}`, `/${newLocale}`);
      } else {
        if (pathname === "/") {
          newPathname = `/${newLocale}`;
        } else {
          newPathname = `/${newLocale}${pathname}`;
        }
      }
      if (newPathname.startsWith("//")) {
        newPathname = newPathname.substring(1);
      }
      // Set the locale cookie manually to ensure persistence
      setCookie("NEXT_LOCALE", newLocale, 30); // Store for 30 days
      // Force a hard refresh to ensure the server picks up the new locale
      setTimeout(() => {
        // This ensures the cookie is written before the navigation
        window.location.href = newPathname;
      }, 50);
    } else {
      console.warn(
        "[LanguageSwitcher] newLocale is NOT in available locales. Aborting switch.",
        { newLocale, availableLocales: locales }
      );
    }
  };

  const currentCountryCode = localeToCountryCode[currentLocale];

  return (
    <Select onValueChange={handleLocaleChange} value={currentLocale}>
      <CustomSelectTrigger className="border-none bg-transparent p-1 p-2.5 shadow-none hover:bg-accent hover:text-accent-foreground focus:ring-0 focus:ring-offset-0">
        <SelectValue placeholder={t("placeholder")}>
          {currentCountryCode ? (
            <div className="flex h-5 w-5 items-center justify-end justify-center">
              <CircleFlag
                countryCode={currentCountryCode}
                height="20"
                width="20"
              />
            </div>
          ) : (
            t("placeholder")
          )}
        </SelectValue>
      </CustomSelectTrigger>
      <SelectContent>
        {locales.map(loc => {
          const countryCode = localeToCountryCode[loc];
          return (
            <SelectItem key={loc} value={loc}>
              <div className="flex items-center justify-end">
                {countryCode ? (
                  <CircleFlag
                    countryCode={countryCode}
                    height="20"
                    width="20"
                    className="mr-2"
                  />
                ) : null}
                {localeEndonyms[loc]}
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
}
